import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Building2, Plus, Search, ChevronRight, ChevronDown, Loader2, MapPin, Folder, LayoutGrid, List, Calendar, Upload, FileSpreadsheet, Info, ArrowUpDown, ArrowUp, ArrowDown, Group, X, Filter, MoreVertical, Trash2, Settings } from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { toast } from 'sonner';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { user, canEditFeature, can, Action } = useAuth();
  const { projects, fetchProjects, createProject, deleteProject, loadProject, activeProject, loading } = useProject();

  const [searchQuery, setSearchQuery] = useState('');
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // View mode: 'list' or 'grid'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('projectViewMode') || 'list';
  });

  // Sorting state
  const [sortField, setSortField] = useState(() => {
    return localStorage.getItem('projectSortField') || 'name';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return localStorage.getItem('projectSortDirection') || 'asc';
  });

  // Grouping state
  const [groupBy, setGroupBy] = useState(() => {
    return localStorage.getItem('projectGroupBy') || 'none';
  });
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const [newProject, setNewProject] = useState({
    name: '',
    code: '',
    code_not_generated: false,
    description: '',
    region: '',
    division: '',
    status: 'active',
    planned_start_date: '',
    data_date: '',
    required_finish_date: ''
  });

  // Filter for projects without code
  const [filterNoCode, setFilterNoCode] = useState(false);

  // Import mode state for project creation
  const [importMode, setImportMode] = useState(false);
  const [importSource, setImportSource] = useState('primavera');

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Save view mode preference
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('projectViewMode', mode);
  };

  // Save sort preferences
  const handleSortChange = (field) => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      localStorage.setItem('projectSortDirection', newDirection);
    } else {
      setSortField(field);
      setSortDirection('asc');
      localStorage.setItem('projectSortField', field);
      localStorage.setItem('projectSortDirection', 'asc');
    }
  };

  // Save group preference
  const handleGroupChange = (value) => {
    setGroupBy(value);
    localStorage.setItem('projectGroupBy', value);
    setCollapsedGroups({});
  };

  // Toggle group collapse
  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Sort projects
  const sortProjects = useCallback((projectsList) => {
    return [...projectsList].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'code':
          aVal = a.code?.toLowerCase() || '';
          bVal = b.code?.toLowerCase() || '';
          break;
        case 'start_date':
          aVal = a.planned_start_date || '';
          bVal = b.planned_start_date || '';
          break;
        case 'end_date':
          aVal = a.required_finish_date || '';
          bVal = b.required_finish_date || '';
          break;
        case 'status':
          aVal = a.status?.toLowerCase() || '';
          bVal = b.status?.toLowerCase() || '';
          break;
        default:
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);

  // Group projects
  const groupProjects = useCallback((projectsList) => {
    if (groupBy === 'none') {
      return { 'All Projects': projectsList };
    }

    const groups = {};
    projectsList.forEach(project => {
      let groupKey;
      switch (groupBy) {
        case 'region':
          groupKey = project.region || 'No Region';
          break;
        case 'division':
          groupKey = project.division || 'No Division';
          break;
        case 'status':
          groupKey = project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1).replace('_', ' ') : 'Unknown';
          break;
        default:
          groupKey = 'All Projects';
      }
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(project);
    });

    return groups;
  }, [groupBy]);

  const filteredProjects = projects.filter(project => {
    // Apply "no code" filter
    if (filterNoCode) {
      const hasNoCode = !project.code || project.code_not_generated;
      if (!hasNoCode) return false;
    }

    // Apply search filter
    const query = searchQuery.toLowerCase();
    return (
      project.name.toLowerCase().includes(query) ||
      project.code?.toLowerCase().includes(query) ||
      project.description?.toLowerCase().includes(query) ||
      project.region?.toLowerCase().includes(query) ||
      project.division?.toLowerCase().includes(query)
    );
  });

  // Process projects with sorting and grouping
  const processedProjects = useMemo(() => {
    const sorted = sortProjects(filteredProjects);
    return groupProjects(sorted);
  }, [filteredProjects, sortProjects, groupProjects]);

  // Sort field display names
  const sortFieldLabels = {
    name: 'Project Name',
    code: 'Project Code',
    start_date: 'Start Date',
    end_date: 'End Date',
    status: 'Status'
  };

  // Group by display names
  const groupByLabels = {
    none: 'No Grouping',
    region: 'Region',
    division: 'Division',
    status: 'Status'
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    // Validate code is provided when code_not_generated is false
    if (!newProject.code_not_generated && !newProject.code?.trim()) {
      toast.error('Project code is required. Check "Code not yet generated" if the code is pending.');
      return;
    }

    // Date-order guard (mirrors backend validate_date_order): Required Finish
    // must not be before Planned Start. ISO yyyy-mm-dd strings compare
    // chronologically, so a lexicographic compare is correct here.
    if (
      newProject.planned_start_date &&
      newProject.required_finish_date &&
      newProject.required_finish_date < newProject.planned_start_date
    ) {
      toast.error('Required Finish cannot be before Planned Start.');
      return;
    }

    setIsCreating(true);
    try {
      const projectData = {
        name: newProject.name.trim(),
        code: newProject.code_not_generated ? null : (newProject.code?.trim() || null),
        code_not_generated: newProject.code_not_generated,
        description: newProject.description?.trim() || null,
        region: newProject.region?.trim() || null,
        division: newProject.division?.trim() || null,
        status: newProject.status || 'active',
        planned_start_date: newProject.planned_start_date || null,
        data_date: newProject.data_date || null,
        required_finish_date: newProject.required_finish_date || null
        // RBAC v2: the creator IS the owner — owner_id and the PLAN membership
        // row are stamped server-side; there is no planner picker.
      };

      const created = await createProject(projectData);
      toast.success('Project created successfully');
      setShowCreateDialog(false);
      setNewProject({
        name: '',
        code: '',
        code_not_generated: false,
        description: '',
        region: '',
        division: '',
        status: 'active',
        planned_start_date: '',
        data_date: '',
        required_finish_date: ''
      });

      // Navigate to the new project
      await loadProject(created.id);
      navigate(`/projects/${created.id}`);
    } catch (error) {
      const message = apiErrorMessage(error, 'Failed to create project');
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectProject = async (project) => {
    if (activeProject && project.id !== activeProject.id) {
      // Already in a project — open the new one in a new tab
      window.open(`/projects/${project.id}`, '_blank');
      return;
    }

    try {
      await loadProject(project.id);
      navigate('/schedules');
    } catch (error) {
      toast.error('Failed to load project');
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    setIsDeleting(true);
    try {
      await deleteProject(projectToDelete.id);
      toast.success(`"${projectToDelete.name}" deleted`);
      setProjectToDelete(null);
    } catch {
      toast.error('Failed to delete project');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Projects
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your construction projects
          </p>
        </div>

        {can(Action.CREATE_PROJECT) && (
          <Button
            className="h-9 bg-slate-900 text-white hover:bg-slate-800 font-medium gap-2"
            onClick={() => setShowCreateDialog(true)}
            data-testid="new-project-button"
          >
            <Plus className="h-4 w-4" />
            New Project
          </Button>
        )}
      </div>

      {/* Search, Sort, Group and View Toggle */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search projects..."
            className="pl-9 h-9 text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-projects-input"
          />
        </div>

        {/* Sort Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-2 text-sm" data-testid="sort-dropdown">
              {sortDirection === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
              Sort: {sortFieldLabels[sortField]}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs text-slate-500">Sort by</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {Object.entries(sortFieldLabels).map(([field, label]) => (
              <DropdownMenuItem
                key={field}
                onClick={() => handleSortChange(field)}
                className="flex items-center justify-between"
                data-testid={`sort-option-${field}`}
              >
                <span>{label}</span>
                {sortField === field && (
                  sortDirection === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Group Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={groupBy !== 'none' ? 'secondary' : 'outline'}
              size="sm"
              className="h-9 gap-2 text-sm"
              data-testid="group-dropdown"
            >
              <Group className="h-3.5 w-3.5" />
              {groupBy !== 'none' ? `Group: ${groupByLabels[groupBy]}` : 'Group'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs text-slate-500">Group by</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {Object.entries(groupByLabels).map(([key, label]) => (
              <DropdownMenuItem
                key={key}
                onClick={() => handleGroupChange(key)}
                className={groupBy === key ? 'bg-slate-100' : ''}
                data-testid={`group-option-${key}`}
              >
                {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Filter: No Code */}
        <Button
          variant={filterNoCode ? 'secondary' : 'outline'}
          size="sm"
          className="h-9 gap-2 text-sm"
          onClick={() => setFilterNoCode(!filterNoCode)}
          data-testid="filter-no-code"
        >
          <Filter className="h-3.5 w-3.5" />
          {filterNoCode ? 'Without Code' : 'No Code Filter'}
        </Button>

        {/* Clear Grouping Button */}
        {groupBy !== 'none' && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2 text-slate-500 hover:text-slate-700"
            onClick={() => handleGroupChange('none')}
            data-testid="clear-grouping"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* View Toggle */}
        <div className="flex items-center border border-slate-200 rounded-md p-0.5" data-testid="view-toggle">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleViewModeChange('list')}
            title="List view"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleViewModeChange('grid')}
            title="Grid view"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Projects Table/Grid */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900">
              All Projects
            </CardTitle>
            <Badge variant="outline" className="text-[10px]">
              {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className={viewMode === 'grid' ? 'p-4' : 'p-0'}>
          {loading && projects.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <Building2 className="h-6 w-6 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-900 mb-1">
                {searchQuery
                  ? 'No projects found'
                  : can(Action.CREATE_PROJECT)
                    ? 'No projects yet'
                    : 'No projects assigned'}
              </p>
              <p className="text-xs text-slate-400 mb-3">
                {searchQuery
                  ? 'Try a different search term'
                  : can(Action.CREATE_PROJECT)
                    ? 'Create your first project to get started'
                    : 'No projects assigned yet — your admin will grant you access.'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View with Grouping */
            <div className="space-y-6">
              {Object.entries(processedProjects).map(([groupName, groupProjects]) => {
                const isCollapsed = collapsedGroups[groupName];
                const showGroupHeader = groupBy !== 'none';

                const getStatusBadge = (status) => {
                  const statusConfig = {
                    active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                    completed: { label: 'Completed', className: 'bg-slate-100 text-slate-500 border-slate-200' },
                    on_hold: { label: 'On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' },
                    planning: { label: 'Planning', className: 'bg-blue-50 text-blue-700 border-blue-200' },
                  };
                  const config = statusConfig[status] || statusConfig.active;
                  return (
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 font-medium ${config.className}`}>
                      {config.label}
                    </Badge>
                  );
                };

                return (
                  <div key={groupName} data-testid={`group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
                    {showGroupHeader && (
                      <div
                        className="flex items-center gap-2 mb-3 cursor-pointer hover:bg-slate-50 rounded-md p-2 -ml-2"
                        onClick={() => toggleGroupCollapse(groupName)}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        )}
                        <span className="text-sm font-semibold text-slate-700">{groupName}</span>
                        <Badge variant="outline" className="text-[10px]">{groupProjects.length}</Badge>
                      </div>
                    )}

                    {!isCollapsed && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {groupProjects.map((project) => (
                          <div
                            key={project.id}
                            className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 hover:shadow-sm cursor-pointer transition-all"
                            onClick={() => handleSelectProject(project)}
                            data-testid={`project-card-${project.id}`}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                <Building2 className="h-5 w-5 text-slate-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{project.name}</p>
                                <span className={`block truncate text-[10px] font-mono ${project.code_not_generated ? 'text-amber-500 italic' : 'text-slate-400'}`}>
                                  {project.code || (project.code_not_generated ? 'Pending' : 'No Code')}
                                </span>
                              </div>
                              {canEditFeature('projects') && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0">
                                      <MoreVertical className="h-4 w-4 text-slate-400" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleSelectProject(project); }}>
                                      Open
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}>
                                      <Settings className="h-3.5 w-3.5 mr-2" />
                                      Project Settings
                                    </DropdownMenuItem>
                                    {/* Delete is plan-level on THIS project (backend: DELETE
                                        /projects requires MANAGE_SCHEDULES). Gate per-project so
                                        view/collaborate/non-members don't see a 403 affordance. */}
                                    {can(Action.MANAGE_SCHEDULES, project.my_role) && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="text-red-600 focus:text-red-600"
                                          onClick={(e) => { e.stopPropagation(); setProjectToDelete(project); }}
                                        >
                                          <Trash2 className="h-3.5 w-3.5 mr-2" />
                                          Delete
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>

                            {project.description && (
                              <p className="text-xs text-slate-500 mb-3 line-clamp-2">
                                {project.description}
                              </p>
                            )}

                            <div className="space-y-2 mb-3">
                              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                <MapPin className="h-3 w-3 text-slate-400" />
                                <span className="truncate">{project.region || 'Default Region'}</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                <Folder className="h-3 w-3 text-slate-400" />
                                <span className="truncate">{project.division || 'Default Division'}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                              {getStatusBadge(project.status)}
                              <span className="text-[10px] text-slate-400">
                                {formatDate(project.created_at)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* List View (Table) with Grouping */
            <div className="space-y-4">
              {Object.entries(processedProjects).map(([groupName, groupProjects]) => {
                const isCollapsed = collapsedGroups[groupName];
                const showGroupHeader = groupBy !== 'none';

                const getStatusBadge = (status) => {
                  const statusConfig = {
                    active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                    completed: { label: 'Completed', className: 'bg-slate-100 text-slate-500 border-slate-200' },
                    on_hold: { label: 'On Hold', className: 'bg-amber-50 text-amber-700 border-amber-200' },
                    planning: { label: 'Planning', className: 'bg-blue-50 text-blue-700 border-blue-200' },
                  };
                  const config = statusConfig[status] || statusConfig.active;
                  return (
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 font-medium ${config.className}`}>
                      {config.label}
                    </Badge>
                  );
                };

                return (
                  <div key={groupName} data-testid={`group-list-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
                    {showGroupHeader && (
                      <div
                        className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-slate-50 rounded-md p-2 mx-2"
                        onClick={() => toggleGroupCollapse(groupName)}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        )}
                        <span className="text-sm font-semibold text-slate-700">{groupName}</span>
                        <Badge variant="outline" className="text-[10px]">{groupProjects.length}</Badge>
                      </div>
                    )}

                    {!isCollapsed && (
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                              Code
                            </TableHead>
                            <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                              Project Name
                            </TableHead>
                            <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                              Region / Division
                            </TableHead>
                            <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                              Status
                            </TableHead>
                            <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                              Created
                            </TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {groupProjects.map((project) => (
                            <TableRow
                              key={project.id}
                              className="cursor-pointer hover:bg-slate-50"
                              onClick={() => handleSelectProject(project)}
                              data-testid={`project-row-${project.id}`}
                            >
                              <TableCell>
                                <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                                  project.code_not_generated
                                    ? 'text-amber-600 bg-amber-50 italic'
                                    : 'text-slate-500 bg-slate-100'
                                }`}>
                                  {project.code || (project.code_not_generated ? 'Pending' : '—')}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center">
                                    <Building2 className="h-4 w-4 text-slate-500" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{project.name}</p>
                                    {project.description && (
                                      <p className="text-xs text-slate-400 truncate max-w-[180px]">
                                        {project.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                    <MapPin className="h-3 w-3 text-slate-400" />
                                    {project.region || 'Default Region'}
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <Folder className="h-3 w-3 text-slate-400" />
                                    {project.division || 'Default Division'}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(project.status)}
                              </TableCell>
                              <TableCell>
                                <span className="text-xs text-slate-500 tabular-nums">
                                  {formatDate(project.created_at)}
                                </span>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                {canEditFeature('projects') ? (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-7 w-7">
                                        <MoreVertical className="h-4 w-4 text-slate-400" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => handleSelectProject(project)}>
                                        Open
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}`)}>
                                        <Settings className="h-3.5 w-3.5 mr-2" />
                                        Project Settings
                                      </DropdownMenuItem>
                                      {/* Delete: plan-level on THIS project (see grid view). */}
                                      {can(Action.MANAGE_SCHEDULES, project.my_role) && (
                                        <>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            className="text-red-600 focus:text-red-600"
                                            onClick={() => setProjectToDelete(project)}
                                          >
                                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                                            Delete
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-slate-400" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Project Panel - Lightweight Side Panel */}
      <Sheet open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <SheetContent
          side="right"
          className="w-full sm:w-[480px] sm:max-w-[480px] p-0 border-l border-slate-200"
        >
          <form onSubmit={handleCreateProject} className="flex flex-col h-full">
            {/* Header */}
            <SheetHeader className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <SheetTitle className="text-base font-semibold text-slate-900">Create New Project</SheetTitle>
              <p className="text-xs text-slate-500 mt-0.5">Enter project details or import from external source.</p>
            </SheetHeader>

            {/* Creation Mode Toggle - Manual vs Import */}
            <div className="px-6 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-lg">
                <Button
                  type="button"
                  variant={!importMode ? 'secondary' : 'ghost'}
                  size="sm"
                  className={`flex-1 h-8 text-xs gap-1.5 ${!importMode ? 'bg-white shadow-sm' : 'hover:bg-slate-200'}`}
                  onClick={() => setImportMode(false)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Manual Entry
                </Button>
                <Button
                  type="button"
                  variant={importMode ? 'secondary' : 'ghost'}
                  size="sm"
                  className={`flex-1 h-8 text-xs gap-1.5 ${importMode ? 'bg-white shadow-sm' : 'hover:bg-slate-200'}`}
                  onClick={() => setImportMode(true)}
                >
                  <Upload className="h-3.5 w-3.5" />
                  Import Project
                </Button>
              </div>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {importMode ? (
                /* Import Mode UI */
                <div className="space-y-5">
                  <div className="space-y-3">
                    <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Import Source</h3>

                    {/* Import Source Selection */}
                    <div className="grid grid-cols-2 gap-3">
                      <div
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${importSource === 'primavera'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                          }`}
                        onClick={() => setImportSource('primavera')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="h-8 w-8 rounded bg-orange-100 flex items-center justify-center">
                            <FileSpreadsheet className="h-4 w-4 text-orange-600" />
                          </div>
                          <span className="text-sm font-medium text-slate-700">Primavera P6</span>
                        </div>
                        <p className="text-[10px] text-slate-400">.xer, .xml files</p>
                      </div>

                      <div
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${importSource === 'msproject'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                          }`}
                        onClick={() => setImportSource('msproject')}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="h-8 w-8 rounded bg-green-100 flex items-center justify-center">
                            <FileSpreadsheet className="h-4 w-4 text-green-600" />
                          </div>
                          <span className="text-sm font-medium text-slate-700">MS Project</span>
                        </div>
                        <p className="text-[10px] text-slate-400">.xml files only</p>
                      </div>
                    </div>
                  </div>

                  {/* Import Info */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex gap-3">
                      <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-700">
                        <p className="font-medium mb-1">Import will create:</p>
                        <ul className="list-disc list-inside text-blue-600 space-y-0.5">
                          <li>Project with schedule</li>
                          <li>WBS hierarchy</li>
                          <li>Activities & relationships</li>
                        </ul>
                        <p className="mt-2 text-blue-500">Go to Import/Export page for full import functionality.</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-10 text-sm gap-2"
                    onClick={() => {
                      setShowCreateDialog(false);
                      navigate('/import-export');
                    }}
                  >
                    <Upload className="h-4 w-4" />
                    Go to Import Page
                  </Button>
                </div>
              ) : (
                /* Manual Entry Mode UI */
                <div className="space-y-5">
                  {/* Project Identity Section */}
                  <div className="space-y-3">
                    <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Project Identity</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2 space-y-1.5">
                        <Label htmlFor="name" className="text-xs font-medium text-slate-700">
                          Project Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          placeholder="Enter project name"
                          value={newProject.name}
                          onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                          className="h-8 text-sm"
                          data-testid="project-name-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="code" className="text-xs font-medium text-slate-700">
                          Code {!newProject.code_not_generated && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          id="code"
                          placeholder={newProject.code_not_generated ? 'N/A' : 'e.g., PRJ-001'}
                          value={newProject.code}
                          onChange={(e) => setNewProject({ ...newProject, code: e.target.value.toUpperCase() })}
                          className={`h-8 text-xs font-mono ${newProject.code_not_generated ? 'bg-slate-50 text-slate-400' : ''}`}
                          maxLength={20}
                          disabled={newProject.code_not_generated}
                          data-testid="project-code-input"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="code_not_generated"
                        checked={newProject.code_not_generated}
                        onCheckedChange={(checked) => setNewProject({
                          ...newProject,
                          code_not_generated: !!checked,
                          code: checked ? '' : newProject.code
                        })}
                        data-testid="code-not-generated-checkbox"
                      />
                      <Label
                        htmlFor="code_not_generated"
                        className="text-xs text-slate-500 cursor-pointer select-none"
                      >
                        Code not yet generated
                      </Label>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="description" className="text-xs font-medium text-slate-700">
                        Description
                      </Label>
                      <Input
                        id="description"
                        placeholder="Brief project description (optional)"
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                        className="h-8 text-sm"
                        data-testid="project-description-input"
                      />
                    </div>
                  </div>

                  {/* Organization Section */}
                  <div className="space-y-3">
                    <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Organization</h3>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="region" className="text-xs font-medium text-slate-700">
                          Region
                        </Label>
                        <Input
                          id="region"
                          placeholder="e.g., North America"
                          value={newProject.region}
                          onChange={(e) => setNewProject({ ...newProject, region: e.target.value })}
                          className="h-8 text-sm"
                          data-testid="project-region-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="division" className="text-xs font-medium text-slate-700">
                          Division
                        </Label>
                        <Input
                          id="division"
                          placeholder="e.g., Commercial"
                          value={newProject.division}
                          onChange={(e) => setNewProject({ ...newProject, division: e.target.value })}
                          className="h-8 text-sm"
                          data-testid="project-division-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="status" className="text-xs font-medium text-slate-700">
                        Status
                      </Label>
                      <Select
                        value={newProject.status}
                        onValueChange={(value) => setNewProject({ ...newProject, status: value })}
                      >
                        <SelectTrigger className="h-8 text-sm" data-testid="project-status-select">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="planning">Planning</SelectItem>
                          <SelectItem value="on_hold">On Hold</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Schedule Dates Section - Grouped for Proximity */}
                  <div className="space-y-3">
                    <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Schedule Dates</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="planned_start_date" className="text-xs font-medium text-slate-700">
                          Planned Start
                        </Label>
                        <Input
                          id="planned_start_date"
                          type="date"
                          value={newProject.planned_start_date}
                          onChange={(e) => setNewProject({ ...newProject, planned_start_date: e.target.value })}
                          className="h-8 text-sm"
                          data-testid="project-start-date-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="data_date" className="text-xs font-medium text-slate-700">
                          Data Date
                        </Label>
                        <Input
                          id="data_date"
                          type="date"
                          value={newProject.data_date}
                          onChange={(e) => setNewProject({ ...newProject, data_date: e.target.value })}
                          className="h-8 text-sm"
                          data-testid="project-data-date-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="required_finish_date" className="text-xs font-medium text-slate-700">
                          Required Finish
                        </Label>
                        <Input
                          id="required_finish_date"
                          type="date"
                          value={newProject.required_finish_date}
                          onChange={(e) => setNewProject({ ...newProject, required_finish_date: e.target.value })}
                          className="h-8 text-sm"
                          data-testid="project-finish-date-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-start gap-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateDialog(false)}
                disabled={isCreating}
                className="h-8 px-4 text-sm"
              >
                Cancel
              </Button>
              {!importMode && (
                <Button
                  type="submit"
                  size="sm"
                  className="h-8 px-4 text-sm bg-slate-900 hover:bg-slate-800"
                  disabled={isCreating}
                  data-testid="create-project-submit"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Project'
                  )}
                </Button>
              )}
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!projectToDelete} onOpenChange={(open) => !open && setProjectToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete Project
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{projectToDelete?.name}</strong>?
              This will permanently remove all schedules, activities, WBS nodes, baselines,
              resources, and calendars associated with this project. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteProject}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                'Delete Project'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsPage;
